import React from 'react'
import { motion } from 'framer-motion'
import Card from './Card'
import Button from './Button'
import { AlertTriangle, RefreshCw, Home } from 'lucide-react'
import errorTracker from '../services/errorTracking'

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true }
  }

  componentDidCatch(error, errorInfo) {
    // Log error to error tracking service
    console.error('Error caught by boundary:', error, errorInfo)
    
    // Send to error tracking service
    errorTracker.captureException(error, {
      react: {
        componentStack: errorInfo.componentStack,
      },
      boundary: true,
      url: window.location.href,
    })

    this.setState({
      error,
      errorInfo,
    })
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null })
    window.location.href = '/dashboard'
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-2xl"
          >
            <Card variant="glass" className="p-8">
              <div className="text-center mb-6">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle size={40} className="text-red-600" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">Something went wrong</h1>
                <p className="text-gray-600 dark:text-gray-400">
                  We're sorry, but something unexpected happened. Our team has been notified.
                </p>
              </div>

              {import.meta.env.DEV && this.state.error && (
                <Card variant="soft" className="p-4 mb-6 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700">
                  <h3 className="font-semibold text-red-900 dark:text-red-200 mb-2">Error Details (Development Only)</h3>
                  <pre className="text-xs text-red-800 dark:text-red-300 overflow-auto max-h-64">
                    {this.state.error.toString()}
                    {this.state.errorInfo?.componentStack}
                  </pre>
                </Card>
              )}

              <div className="flex gap-3 justify-center">
                <Button onClick={this.handleReset} className="flex items-center gap-2">
                  <RefreshCw size={18} />
                  Reload Application
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => (window.location.href = '/dashboard')}
                  className="flex items-center gap-2"
                >
                  <Home size={18} />
                  Go to Dashboard
                </Button>
              </div>

              <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-6">
                If this problem persists, please contact support with the error details above.
              </p>
            </Card>
          </motion.div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary

